import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Tab,
  Tabs,
} from "@mui/material";
import { ROLES } from "app/constants";
import { Button, Checkbox, PageHeader, Spacer, Toolbar } from "common";
import I18n from "i18n";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  filterPermissionsByAudience,
  PermissionAudience,
} from "./permissionAudiences";
import usePermissionForm from "./usePermissionForm";

export interface PermissionFormProps {
  showHeader?: boolean;
}

export default function PermissionForm({
  showHeader = false,
}: PermissionFormProps) {
  const { save, permissions, loading, saving, changePermission, isDirty } =
    usePermissionForm();
  const params = useParams<"user_id" | "role_id">();
  const parentForm = useOutletContext<{
    item?: { name?: string; partner_id?: number | null };
  }>();
  const [selectedAudience, setSelectedAudience] =
    useState<PermissionAudience | null>(null);
  const isPartnerTarget = params.role_id
    ? parentForm?.item?.name === ROLES.PARTNER
    : !!parentForm?.item?.partner_id;
  const activeAudience =
    selectedAudience || (isPartnerTarget ? "partner" : "employee");
  const visiblePermissions = filterPermissionsByAudience(
    permissions,
    activeAudience,
  );

  return (
    <>
      {showHeader && (
        <>
          <PageHeader
            avatar={<VerifiedUserIcon />}
            title={I18n.t("Permission.title")}
          />
          <Spacer h={2} />
        </>
      )}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeAudience}
          onChange={(_, value: PermissionAudience) =>
            setSelectedAudience(value)
          }
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="partner" label={I18n.t("Permission.audiences.partner")} />
          <Tab
            value="employee"
            label={I18n.t("Permission.audiences.employee")}
          />
        </Tabs>
      </Box>
      <Grid container spacing={2}>
        {visiblePermissions.map(permission => {
          return (
            <Grid item xs={12} md={6} key={permission.model} display="flex">
              <Card
                elevation={0}
                sx={{ flex: 1, border: 1, borderColor: "divider" }}
              >
                <CardHeader title={I18n.t(`${permission.model}.title`)} />
                <CardContent>
                  {permission.actions.map(action => {
                    return (
                      <Checkbox
                        disabled={action.disabled}
                        onChange={(evt: any) => {
                          changePermission(action.permission, evt.target.value);
                        }}
                        key={action.id}
                        value={action.value}
                        label={I18n.t(`Permission.actions.${action.name}`)}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Spacer h={2} />
      <Toolbar>
        <Spacer right />
        <Button
          disabled={!isDirty}
          loading={saving}
          title={I18n.t("App.save")}
          onClick={() => save()}
        />
      </Toolbar>
    </>
  );
}
